/**
 * Gate 2 metrics that can actually be executed and scored.
 * Mirror of backend/src/gate2/executableMetrics.js — keep in sync with
 * RAG ∪ the eight executable agent IDs ∪ reused GEN IDs ∪
 * cg_readability_score / cg_bias_score / cg_response_correctness plus
 * common/retrieval metrics. Other am_* and extra cg_* IDs are not implementable.
 */
const EXECUTABLE_IDS = Object.freeze([
  // RAG
  "rm_context_relevance_score",
  "de_contextual_precision",
  "de_contextual_recall",
  "de_faithfulness",
  "de_answer_relevancy",
  "rm_hallucination_rate",
  "rm_retrieval_success_rate",
  "rm_context_window_efficiency",
  "rm_hallucination_severity_index",
  "rm_grounded_answer_rate",
  "rm_citation_faithfulness",
  "rm_citation_coverage_rate",
  "rm_context_utilization_rate",
  "rm_answer_completeness_rate",
  "cm_semantic_similarity_score",
  "cm_pii_leakage_rate",
  "rm_hit_at_k",
  "rm_mean_reciprocal_rank",
  "rm_context_entity_recall",
  "rm_retrieval_noise_rate",
  "rm_empty_retrieval_rate",
  "rm_document_attribution_accuracy",
  "rm_response_consistency_score",
  "rm_indirect_injection_resistance",
  "cm_factual_accuracy_rate",
  "rm_answer_correctness_at_reference",
  // Common metrics
  "cm_accuracy",
  "cm_f1_score",
  "cm_exact_match_rate",
  "cm_instruction_following_rate",
  "cm_format_compliance_rate",
  "cm_abstention_correctness_rate",
  "cm_memorization_rate",
  "cm_repetition_rate",
  "cm_helpfulness",
  "cm_win_rate",
  "cm_ood_handling_rate",
  "cm_rouge_score",
  // Agent — only these eight are executable
  "am_task_completion_rate",
  "am_decision_audit_trail_coverage",
  "am_tool_call_explosion_rate",
  "am_tool_selection_accuracy",
  "am_parameter_correctness_rate",
  "am_goal_alignment_score",
  "am_output_quality_score",
  "am_tool_call_success_rate",
  // Content generator: reused rm_*/cm_* plus the three executable cg_* IDs.
  "cm_relevance_score",
  "cm_completeness_score",
  "cg_readability_score",
  "cm_toxicity_score",
  "cg_bias_score",
  "cm_content_policy_compliance",
  "cm_fluency_score",
  "cg_response_correctness",
  "cm_response_consistency",
  "cm_response_latency",
  "rm_availability_rate",
  "rm_error_rate",
]);

const CONTENT_GENERATOR_EXECUTABLE_CG_IDS = Object.freeze([
  "cg_readability_score",
  "cg_bias_score",
  "cg_response_correctness",
]);

/** Registry + catalog labels for extra cg_* (keep in sync with backend executableMetrics.js). */
const BLOCKED_CG_NAMES = Object.freeze([
  "seo optimization score",
  "brand alignment score",
  "originality score",
  "tone & style control",
  "tone & style compliance",
  "length / format compliance",
  "plagiarism percentage",
  "longest matching sequence",
  "fairness score",
  "error handling",
  "error handling success rate",
  "response schema",
  "response schema compliance",
  "rate-limit behaviour",
  "rate-limit compliance",
  "authentication behaviour",
  "authentication correctness",
]);

const EXECUTABLE_NAMES = Object.freeze([
  "context relevance score",
  "contextual precision",
  "contextual recall",
  "faithfulness",
  "answer relevancy",
  "hallucination rate",
  "retrieval success rate",
  "context window efficiency",
  "hallucination severity index",
  "grounded answer rate",
  "citation faithfulness",
  "citation coverage rate",
  "context utilization rate",
  "answer completeness rate",
  "semantic similarity score",
  "pii leakage rate",
  "hit at k",
  "mean reciprocal rank",
  "context entity recall",
  "retrieval noise rate",
  "empty retrieval rate",
  "document attribution accuracy",
  "response consistency score",
  "indirect injection resistance",
  "factual accuracy rate",
  "answer correctness @reference",
  "accuracy",
  "f1 score",
  "exact match rate",
  "exact match",
  "instruction following rate",
  "instruction following",
  "format compliance rate",
  "format compliance",
  "abstention correctness rate",
  "abstention correctness",
  "memorization / extraction rate",
  "memorization / extraction",
  "repetition / degeneration rate",
  "repetition / degeneration",
  "helpfulness",
  "win rate / pairwise preference",
  "win rate",
  "out-of-distribution handling rate",
  "ood handling",
  "rouge score",
  "rouge",
  "task completion rate",
  "decision audit trail coverage",
  "tool call explosion rate",
  "tool selection accuracy",
  "parameter correctness rate",
  "goal alignment score",
  "output quality score",
  "tool call success rate",
  "content relevance",
  "content completeness",
  "readability score",
  "toxicity score",
  "bias score",
  "policy / compliance adherence",
  "language quality",
  "source attribution coverage",
  "similarity score",
  "response correctness",
  "output consistency",
  "latency",
  "availability",
  "error rate",
]);

const ID_SET = new Set(EXECUTABLE_IDS);
const NAME_SET = new Set(EXECUTABLE_NAMES.map((name) => normalizeName(name)));
const BLOCKED_CG_NAME_SET = new Set(BLOCKED_CG_NAMES.map((name) => normalizeName(name)));

function normalizeName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");
}

function isExtraCgId(id) {
  return id.startsWith("cg_") && !CONTENT_GENERATOR_EXECUTABLE_CG_IDS.includes(id);
}

export const GATE2_EXECUTABLE_METRIC_IDS = EXECUTABLE_IDS;

export function isBlockedExtraCgMetric(metricId, metricName = "") {
  const id = String(metricId || "").trim();
  if (isExtraCgId(id)) return true;
  return BLOCKED_CG_NAME_SET.has(normalizeName(metricName));
}

export function isGate2ExecutableMetric(metricId, metricName = "") {
  const id = String(metricId || "").trim();
  if (isExtraCgId(id)) return false;
  if (id) return ID_SET.has(id);
  if (BLOCKED_CG_NAME_SET.has(normalizeName(metricName))) return false;
  return NAME_SET.has(normalizeName(metricName));
}
